import React, { useEffect, useMemo, useState } from 'react';
import { Container, Typography, Box, Button, CircularProgress, IconButton, Tabs, Tab, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/rootReducer';
import { createAllEventsColumns, AllEventsRow, rowDate } from './allEventsColumn';
import { createBulkUploadEventsColumns } from './bulkUploadEventsColumn';
import { actions as administratorHistoryActions } from '../../redux/modules/administratorHistory';
import { actions as bulkUploadBatchesActions, BulkUploadBatchStatus } from '../../redux/modules/bulkUploadBatches';
import styles from './styles';
import { ModalPage } from 'nav/ModalPage';

const STATUS_CHIP_COLOR: Record<BulkUploadBatchStatus, 'success' | 'warning' | 'error' | 'default'> = {
  success: 'success',
  partial: 'warning',
  failed: 'error',
  unknown: 'default',
};

const STATUS_CHIP_LABEL: Record<BulkUploadBatchStatus, string> = {
  success: 'Success',
  partial: 'Partial',
  failed: 'Failed',
  // Batches persisted before outcome tracking existed — the split was never recorded.
  unknown: 'Unknown',
};

const AdministratorHistoryPage: ModalPage<{}> = (props) => {
  const dispatch = useDispatch();
  const { adminHistory, loading, error } = useSelector((state: RootState) => state.administratorHistory);
  const { batches, selectedBatch, detailLoading, detailError } = useSelector(
    (state: RootState) => state.bulkUploadBatches,
  );
  const { onClickCancel } = props;

  const [pageSize, setPageSize] = useState(5);
  const [eventsPageSize, setEventsPageSize] = useState(8);
  const [tab, setTab] = useState<'all' | 'bulk'>('all');

  useEffect(() => {
    dispatch(administratorHistoryActions.get());
    dispatch(bulkUploadBatchesActions.getList());
  }, [dispatch]);

  // The only way into the 'bulk' tab: clicking "View details" on a collapsed Bulk Upload row
  // in the All Events table.
  const onClickViewDetails = (batchId: string) => {
    dispatch(bulkUploadBatchesActions.getDetail({ id: batchId }));
    setTab('bulk');
  };

  const handleTabChange = (_: React.SyntheticEvent, value: 'all' | 'bulk') => {
    if (tab === 'bulk' && value !== 'bulk') {
      dispatch(bulkUploadBatchesActions.clearDetail());
    }
    setTab(value);
  };

  const allEventsRows: AllEventsRow[] = useMemo(() => {
    const historyRows: AllEventsRow[] = adminHistory.map((history) => ({
      isBulkSummary: false,
      ...history,
    }));
    const bulkRows: AllEventsRow[] = batches.map((batch) => ({
      isBulkSummary: true,
      id: batch.id,
      batch,
    }));
    return [...historyRows, ...bulkRows].sort(
      (a, b) => new Date(rowDate(b)).getTime() - new Date(rowDate(a)).getTime(),
    );
  }, [adminHistory, batches]);

  const allEventsColumns = useMemo(() => createAllEventsColumns(onClickViewDetails), []);
  const bulkUploadEventsColumns = useMemo(() => createBulkUploadEventsColumns(), []);

  const closePage = () => {
    dispatch(administratorHistoryActions.setError({ error: undefined }));
    dispatch(bulkUploadBatchesActions.clearDetail());
    onClickCancel();
  };

  return (
    <Container component="main">
      <Box sx={styles.modal}>
        <IconButton
          aria-label="Close user activity modal"
          onClick={closePage}
          sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={styles.scrollableContent}>
          <Typography variant="h4" sx={styles.headerLabel}>
            User Activity
          </Typography>

          <Tabs value={tab} onChange={handleTabChange} sx={styles.tabBar}>
            <Tab label="All Events" value="all" />
            {(tab === 'bulk' || selectedBatch) && <Tab label="Bulk Upload" value="bulk" />}
          </Tabs>

          {tab === 'all' &&
            (loading ? (
              <Typography>Loading...</Typography>
            ) : error ? (
              <Typography color="error">{error}</Typography>
            ) : (
              <DataGrid
                rows={allEventsRows}
                columns={allEventsColumns}
                autoHeight
                pagination
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[5, 10]}
                getRowId={(row) => row.id}
              />
            ))}

          {tab === 'bulk' &&
            (detailLoading ? (
              <Typography>Loading...</Typography>
            ) : detailError ? (
              <Typography color="error">{detailError}</Typography>
            ) : selectedBatch ? (
              <>
                <Box sx={styles.bulkHeader}>
                  <Typography variant="h6">{selectedBatch.fileName ?? '--'}</Typography>
                  <Box sx={styles.bulkMeta}>
                    <Typography variant="body2" component="span">
                      Uploaded by {selectedBatch.user?.email ?? '--'}
                    </Typography>
                    <Typography variant="body2" component="span">
                      {new Date(selectedBatch.createdAt).toISOString().split('T')[0]}
                    </Typography>
                    <Chip
                      label={STATUS_CHIP_LABEL[selectedBatch.status]}
                      color={STATUS_CHIP_COLOR[selectedBatch.status]}
                      size="small"
                    />
                  </Box>
                </Box>

                <Box sx={styles.statRow}>
                  <Box sx={styles.statTile}>
                    <Typography sx={styles.statTileValue} variant="h6">
                      {selectedBatch.rowCount}
                    </Typography>
                    <Typography variant="body2">rows processed</Typography>
                  </Box>
                  <Box sx={styles.statTile}>
                    <Typography sx={styles.statTileValue} variant="h6">
                      {selectedBatch.patientsCreated}
                    </Typography>
                    <Typography variant="body2">patients created</Typography>
                  </Box>
                  <Box sx={styles.statTile}>
                    <Typography sx={styles.statTileValue} variant="h6">
                      {selectedBatch.devicesAssigned}
                    </Typography>
                    <Typography variant="body2">devices assigned</Typography>
                  </Box>
                  <Box sx={styles.statTile}>
                    <Typography sx={styles.statTileValue} variant="h6">
                      {selectedBatch.patientsUpdated}
                    </Typography>
                    <Typography variant="body2">patients updated</Typography>
                  </Box>
                  <Box sx={styles.statTile}>
                    <Typography
                      sx={
                        (selectedBatch.failedCount ?? 0) > 0
                          ? styles.statTileValueWarn
                          : styles.statTileValue
                      }
                      variant="h6">
                      {selectedBatch.failedCount ?? '—'}
                    </Typography>
                    <Typography variant="body2">rows failed</Typography>
                  </Box>
                </Box>

                <DataGrid
                  rows={selectedBatch.events}
                  columns={bulkUploadEventsColumns}
                  autoHeight
                  pagination
                  pageSize={eventsPageSize}
                  onPageSizeChange={(newPageSize) => setEventsPageSize(newPageSize)}
                  rowsPerPageOptions={[8, 25]}
                  getRowId={(row) => row.id}
                />
              </>
            ) : null)}
        </Box>
        <Box sx={styles.buttonContainer}>
          <Button onClick={closePage} variant="contained">
            Close
          </Button>
          {(loading || detailLoading) && (
            <Box sx={styles.progress}>
              <CircularProgress size={10} />
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default AdministratorHistoryPage;
